import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import Shell from "@/components/Shell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadNew from "@/pages/LeadNew";
import LeadScript from "@/pages/LeadScript";
import LeadDetail from "@/pages/LeadDetail";
import CarePlans from "@/pages/CarePlans";
import TechnicalQualification from "@/pages/TechnicalQualification";
import PublicEstimate from "@/pages/PublicEstimate";
import PublicProposal from "@/pages/PublicProposal";
import PublicCallback from "@/pages/PublicCallback";
import PublicContractData from "@/pages/PublicContractData";
import PublicDAF from "@/pages/PublicDAF";
import DafDashboardPortal from "@/pages/DafDashboardPortal";
import PublicCarePlan from "@/pages/PublicCarePlan";
import PublicPontualCarePlan from "@/pages/PublicPontualCarePlan";
import PublicRtRecap from "@/pages/PublicRtRecap";
import MarginAuthorizationPublic from "@/pages/MarginAuthorizationPublic";
import MarginAuthorizations from "@/pages/MarginAuthorizations";
import PublicCarePlanDTReview from "@/pages/PublicCarePlanDTReview";
import ProposalPrevalidationSettings from "@/pages/ProposalPrevalidationSettings";
import ProposalPrevalidationPublic from "@/pages/ProposalPrevalidationPublic";
import ClientBanner from "@/components/ClientBanner";
import MagicLink from "@/pages/MagicLink";

// Public routes get a thin wrapper that renders the transition banner
// on top of whatever the public page returns. Also enables .public-theme
// (larger radius + Playfair headings + elderly-friendly type scale).
// NOTE: no theme toggle here — public pages (Estimate/Proposal/etc.) are
// designed dark-hero-first with their own --pub-bg/--pub-text palette.
// Letting the client toggle to light mode broke text contrast on those
// dark sections (white-on-white). Kept the .public-theme class so the
// generous type scale + Playfair fonts still apply.
const PublicShell = ({ children }) => (
  <div className="public-theme min-h-screen bg-background text-foreground">
    <ClientBanner />
    {children}
  </div>
);
import PublicDiscountApproval from "@/pages/PublicDiscountApproval";
import PublicScenario from "@/pages/PublicScenario";
import AiInstructionsSettings from "@/pages/AiInstructionsSettings";
import UserManual from "@/pages/UserManual";
import CarePlanGuide from "@/pages/CarePlanGuide";
import ZohoSettings from "@/pages/ZohoSettings";
import ZohoJourneySettings from "@/pages/ZohoJourneySettings";
import LinkusSettings from "@/pages/LinkusSettings";
import LinkusDiscrepancies from "@/pages/LinkusDiscrepancies";
import ClickUpSettings from "@/pages/ClickUpSettings";
import PublicCopySettings from "@/pages/PublicCopySettings";
import UsersSettings from "@/pages/UsersSettings";
import PermissionsSettings from "@/pages/PermissionsSettings";
import BrandingSettings from "@/pages/BrandingSettings";
import PriceTables from "@/pages/PriceTables";
import PricingConfig from "@/pages/PricingConfig";
import EmailSettings from "@/pages/EmailSettings";
import FieldsSettings from "@/pages/FieldsSettings";
import QualificationTagsSettings from "@/pages/QualificationTagsSettings";
import RTNotesSnippetsSettings from "@/pages/RTNotesSnippetsSettings";
import ScriptSettings from "@/pages/ScriptSettings";
import TemplatesSettings from "@/pages/TemplatesSettings";
import ProposalTemplatesList from "@/pages/ProposalTemplatesList";
import ProposalTemplateSettings from "@/pages/ProposalTemplateSettings";
import MaintenanceSettings from "@/pages/MaintenanceSettings";
import OfficesSettings from "@/pages/OfficesSettings";
import ObjectiveBonusDashboard from "@/pages/ObjectiveBonusDashboard";
import ApprovalsSettings from "@/pages/ApprovalsSettings";
import MyBonuses from "@/pages/MyBonuses";
import PerformanceDashboard from "@/pages/PerformanceDashboard";
import OperatorDetail from "@/pages/OperatorDetail";
import PerformanceCompare from "@/pages/PerformanceCompare";
import AlertsPage from "@/pages/AlertsPage";
import BaselinesSettings from "@/pages/BaselinesSettings";
import BonusesOverview from "@/pages/BonusesOverview";
import LeadTimeline from "@/pages/LeadTimeline";
import SystemSettings from "@/pages/SystemSettings";
import Profile from "@/pages/Profile";
import DailyReport from "@/pages/DailyReport";
import OfficePerformanceReport from "@/pages/OfficePerformanceReport";
import DAFReconcile from "@/pages/DAFReconcile";
import Deposits from "@/pages/Deposits";
import PendingProposals from "@/pages/PendingProposals";
import AcceptedMargins from "@/pages/AcceptedMargins";
import RecruitmentOverrunPublic from "@/pages/RecruitmentOverrunPublic";
import RecruitmentMarginPublic from "@/pages/RecruitmentMarginPublic";
import DailyReportConfigSettings from "@/pages/DailyReportConfigSettings";
import EstimateRulesSettings from "@/pages/EstimateRulesSettings";
import Followups from "@/pages/Followups";
import RTsPendentes from "@/pages/RTsPendentes";
import RTsCalendar from "@/pages/RTsCalendar";
import AttendanceAdminPage from "@/pages/AttendanceAdminPage";
import JustifyLead from "@/pages/JustifyLead";
import { BrandingProvider } from "@/lib/branding";
import { RoleGate } from "@/lib/permissions";
import CallBanner from "@/components/CallBanner";
import "@/index.css";

const Admin = ({ children }) => <RoleGate routeKey="admin">{children}</RoleGate>;

// Track live html.dark class so the Toaster can adopt matching palette.
function useThemeClass() {
  const [theme, setTheme] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light"
  );
  useEffect(() => {
    if (typeof document === "undefined") return;
    const obs = new MutationObserver(() => {
      setTheme(document.documentElement.classList.contains("dark") ? "dark" : "light");
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return theme;
}

function App() {
  const theme = useThemeClass();
  return (
    <BrandingProvider>
    <div className="min-h-screen bg-background text-foreground font-body">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/auth/magic" element={<MagicLink />} />
          <Route path="/estimate/:token" element={<PublicShell><PublicEstimate /></PublicShell>} />
          <Route path="/proposal/:token" element={<PublicShell><PublicProposal /></PublicShell>} />
          <Route path="/proposal/:token/contract-data" element={<PublicShell><PublicContractData /></PublicShell>} />
          <Route path="/daf/:token" element={<PublicShell><PublicDAF /></PublicShell>} />
          <Route path="/daf-dashboard" element={<DafDashboardPortal />} />
          <Route path="/care-plan/:token" element={<PublicShell><PublicCarePlan /></PublicShell>} />
          <Route path="/care-plan/pontual/:token" element={<PublicShell><PublicPontualCarePlan /></PublicShell>} />
          <Route path="/dt-review/:token" element={<PublicCarePlanDTReview />} />
          <Route path="/rt-recap/:token" element={<PublicShell><PublicRtRecap /></PublicShell>} />
          <Route path="/margin-authorization/:token" element={<PublicShell><MarginAuthorizationPublic /></PublicShell>} />
          <Route path="/proposal-prevalidation/:token" element={<ProposalPrevalidationPublic />} />
          <Route path="/recruitment-overrun/:token" element={<PublicShell><RecruitmentOverrunPublic /></PublicShell>} />
          <Route path="/recruitment-margin/:token" element={<PublicShell><RecruitmentMarginPublic /></PublicShell>} />
          <Route path="/estimate-discount-approval/:token" element={<PublicShell><PublicDiscountApproval /></PublicShell>} />
          <Route path="/scenario/:token" element={<PublicShell><PublicScenario /></PublicShell>} />
          <Route path="/callback/:token" element={<PublicShell><PublicCallback /></PublicShell>} />
          <Route path="/justify/:leadId" element={<JustifyLead />} />
          <Route element={<Shell />}>
            <Route path="/dashboard" element={<RoleGate routeKey="dashboard"><Dashboard /></RoleGate>} />
            <Route path="/leads" element={<RoleGate routeKey="leads"><Leads /></RoleGate>} />
            <Route path="/leads/new" element={<RoleGate routeKey="lead-new"><LeadNew /></RoleGate>} />
            <Route path="/leads/script" element={<RoleGate routeKey="lead-new"><LeadScript /></RoleGate>} />
            <Route path="/leads/:id/script" element={<RoleGate routeKey="lead-new"><LeadScript /></RoleGate>} />
            <Route path="/leads/:id" element={<RoleGate routeKey="lead-detail"><LeadDetail /></RoleGate>} />
            <Route path="/leads/:id/edit" element={<RoleGate routeKey="lead-detail"><LeadNew /></RoleGate>} />
            <Route path="/leads/:id/technical" element={<RoleGate routeKey="technical"><TechnicalQualification /></RoleGate>} />
            <Route path="/leads/:id/timeline" element={<RoleGate routeKey="lead-detail"><LeadTimeline /></RoleGate>} />
            <Route path="/care-plans" element={<RoleGate routeKey="care-plans"><CarePlans /></RoleGate>} />
            <Route path="/margin-authorizations" element={<Admin><MarginAuthorizations /></Admin>} />
            <Route path="/settings/branding" element={<Admin><BrandingSettings /></Admin>} />
            <Route path="/settings/pricing" element={<Admin><PricingConfig /></Admin>} />
            <Route path="/settings/pricing-legacy" element={<Admin><PriceTables /></Admin>} />
            <Route path="/settings/email" element={<Admin><EmailSettings /></Admin>} />
            <Route path="/settings/fields" element={<Admin><FieldsSettings /></Admin>} />
            <Route path="/settings/qualification-tags" element={<Admin><QualificationTagsSettings /></Admin>} />
            <Route path="/settings/rt-notes-snippets" element={<Admin><RTNotesSnippetsSettings /></Admin>} />
            <Route path="/settings/script" element={<Admin><ScriptSettings /></Admin>} />
            <Route path="/settings/templates" element={<Admin><TemplatesSettings /></Admin>} />
            <Route path="/settings/proposal-templates" element={<Admin><ProposalTemplatesList /></Admin>} />
            <Route path="/settings/proposal-builder" element={<Admin><ProposalTemplateSettings /></Admin>} />
            <Route path="/settings/proposal-builder/:templateId" element={<Admin><ProposalTemplateSettings /></Admin>} />
            <Route path="/settings/zoho" element={<Admin><ZohoSettings /></Admin>} />
            <Route path="/settings/zoho/journey" element={<Admin><ZohoJourneySettings /></Admin>} />
            <Route path="/settings/linkus" element={<Admin><LinkusSettings /></Admin>} />
            <Route path="/linkus/discrepancies" element={<Admin><LinkusDiscrepancies /></Admin>} />
            <Route path="/settings/clickup" element={<Admin><ClickUpSettings /></Admin>} />
            <Route path="/settings/public-copy" element={<Admin><PublicCopySettings /></Admin>} />
            <Route path="/settings/ai-instructions" element={<Admin><AiInstructionsSettings /></Admin>} />
            <Route path="/settings/users" element={<Admin><UsersSettings /></Admin>} />
            <Route path="/settings/permissions" element={<Admin><PermissionsSettings /></Admin>} />
            <Route path="/settings/maintenance" element={<Admin><MaintenanceSettings /></Admin>} />
            <Route path="/settings/offices" element={<RoleGate routeKey="offices"><OfficesSettings /></RoleGate>} />
            <Route path="/settings/approvals" element={<Admin><ApprovalsSettings /></Admin>} />
            <Route path="/settings/proposal-prevalidation" element={<Admin><ProposalPrevalidationSettings /></Admin>} />
            <Route path="/me/bonuses" element={<RoleGate routeKey="my-bonuses"><MyBonuses /></RoleGate>} />
            <Route path="/performance" element={<RoleGate routeKey="performance"><PerformanceDashboard /></RoleGate>} />
            <Route path="/performance/operators/:id" element={<RoleGate routeKey="performance"><OperatorDetail /></RoleGate>} />
            <Route path="/performance/compare" element={<RoleGate routeKey="performance"><PerformanceCompare /></RoleGate>} />
            <Route path="/alerts" element={<RoleGate routeKey="alerts"><AlertsPage /></RoleGate>} />
            <Route path="/bonuses" element={<Admin><BonusesOverview /></Admin>} />
            <Route path="/settings/baselines" element={<Admin><BaselinesSettings /></Admin>} />
            <Route path="/objectives/:id" element={<RoleGate routeKey="offices"><ObjectiveBonusDashboard /></RoleGate>} />
            <Route path="/settings/system" element={<Admin><SystemSettings /></Admin>} />
            <Route path="/settings/daily-report" element={<Admin><DailyReportConfigSettings /></Admin>} />
            <Route path="/settings/estimate-rules" element={<Admin><EstimateRulesSettings /></Admin>} />
            <Route path="/settings/attendance" element={<Admin><AttendanceAdminPage /></Admin>} />
            <Route path="/profile" element={<RoleGate routeKey="profile"><Profile /></RoleGate>} />
            <Route path="/help" element={<UserManual />} />
            <Route path="/help/care-plan-guide" element={<CarePlanGuide />} />
            <Route path="/reports/daily" element={<RoleGate routeKey="daily-report"><DailyReport /></RoleGate>} />
            <Route path="/reports/offices" element={<RoleGate routeKey="office-report"><OfficePerformanceReport /></RoleGate>} />
            <Route path="/followups" element={<Followups />} />
            <Route path="/rts-pendentes" element={<RoleGate routeKey="rts-pendentes"><RTsPendentes /></RoleGate>} />
            <Route path="/rts-calendar" element={<RoleGate routeKey="rts-calendar"><RTsCalendar /></RoleGate>} />
            <Route path="/daf/reconcile" element={<RoleGate routeKey="finance-daf"><DAFReconcile /></RoleGate>} />
            {/* 25/02/2026 — Cauções: visível a todos (routeKey "deposits" registada em permissions.jsx). */}
            <Route path="/deposits" element={<RoleGate routeKey="deposits"><Deposits /></RoleGate>} />
            <Route path="/pending-proposals" element={<RoleGate routeKey="deposits"><PendingProposals /></RoleGate>} />
            <Route path="/margins" element={<RoleGate routeKey="margins"><AcceptedMargins /></RoleGate>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      {/* Floating call banner — visible on every authenticated page as
          long as an active call exists in localStorage. */}
      <CallBanner />
      <Toaster theme={theme} position="top-right" toastOptions={{ className: "font-body" }} />
    </div>
    </BrandingProvider>
  );
}

export default App;
